import { useMemo } from "react";
import {
  Bar,
  ComposedChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MetricsData, KpiCard } from "@/hooks/useMetrics";

interface ThroughputStabilityChartProps {
  metrics: MetricsData | null;
}

interface ChartDatum {
  label: string;
  deployFrequency: number | null;
  cfr: number | null;
  rework: number | null;
}

function findKpi(kpis: KpiCard[], id: string): KpiCard | undefined {
  return kpis.find((k) => k.id === id);
}

export function ThroughputStabilityChart({
  metrics,
}: ThroughputStabilityChartProps) {
  const { t } = useTranslation();

  const data = useMemo((): ChartDatum[] => {
    if (!metrics?.has_data) return [];

    const deploy = findKpi(metrics.kpis, "deploy_frequency");
    const cfr = findKpi(metrics.kpis, "change_failure_rate");
    const rework = findKpi(metrics.kpis, "rework_rate");

    if (!deploy && !cfr && !rework) return [];

    const current: ChartDatum = {
      label: t("executive.throughput.current"),
      deployFrequency: deploy?.value ?? null,
      cfr: cfr?.value ?? null,
      rework: rework?.value ?? null,
    };

    const previous: ChartDatum = {
      label: t("executive.throughput.previous"),
      deployFrequency:
        deploy ? (deploy.value - (deploy.delta ?? 0)) : null,
      cfr: cfr ? (cfr.value - (cfr.delta ?? 0)) : null,
      rework: rework ? (rework.value - (rework.delta ?? 0)) : null,
    };

    return [previous, current];
  }, [metrics, t]);

  if (data.length === 0) {
    return (
      <Card data-testid="throughput-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            {t("executive.throughput.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("executive.throughput.noData")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="throughput-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("executive.throughput.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart
            data={data}
            margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              tickLine={false}
              axisLine={false}
              width={36}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `${v}%`}
              width={40}
            />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              yAxisId="left"
              dataKey="deployFrequency"
              name={t("executive.throughput.deployFrequency")}
              fill="hsl(210 72% 47%)"
              radius={[2, 2, 0, 0]}
              maxBarSize={40}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cfr"
              name={t("executive.throughput.changeFailureRate")}
              stroke="hsl(0 72% 47%)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="rework"
              name={t("executive.throughput.reworkRate")}
              stroke="hsl(30 72% 47%)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
